import {Box, Flex, Label, P, Title} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {FC, useCallback, useMemo, useState} from 'react'

import {MemberParticipantType} from '../../../api/member/types'
import {Subscription, UnavailableSubscription} from '../../../api/subscriptions/types'
import {SubscriptionCard} from '../../../components/subscription-card/subscription-card'
import {formatOpeningTimes} from '../../../helpers/pass-hours'
import {usePassHoursOptions} from '../../../helpers/use-pass-hours-options'
import {EditParticipantFormType} from '../../../services/booking/forms/edit-participant-form'
import {Button} from '../../../ui/components/button/button'
import {Input} from '../../../ui/components/input/input'
import {RadioButton} from '../../../ui/components/radio-button'
import {useLocale} from '../../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../../utils/logic'
import {NO_USER_PHOTO} from '../../../utils/remote-content'
import {MemberParticipant} from './add-participant-modal'

export const EditParticipantModal: FC<{
  subscriptions?: (Subscription | undefined)[]
  // Passes the person owns that cannot be used at the chosen time. Shown greyed out, never selectable.
  unavailableSubscriptions?: UnavailableSubscription[]
  memberParticipants?: MemberParticipantType[]
  initialMemberParticipantIndex: number | null
  omittedMemberParticipants: number[]
  isLoggedIn: boolean
  allowedSubscriptionsList?: (string | null)[]
  anySubscriptions?: boolean
}> = ({
  isLoggedIn,
  subscriptions,
  unavailableSubscriptions = [],
  memberParticipants = [],
  anySubscriptions = true,
  allowedSubscriptionsList = [],
  omittedMemberParticipants = [],
  initialMemberParticipantIndex = 0,
}) => {
  const {l} = useLocale()
  const passHoursOptions = usePassHoursOptions()
  const {handleSubmit, values, setValues} = useFormikContext<EditParticipantFormType>()
  const [selectedMemberParticipantIndex, setSelectedMemberParticipantIndex] = useState<number | null>(
    isLoggedIn ? initialMemberParticipantIndex : null,
  )
  const [selectedSubscriptionIndex, setSelectedSubscriptionIndex] = useState<number | undefined>(
    subscriptions?.findIndex((el) => el?.membershipId === values.subscription?.membershipId),
  )
  const subscriptionsOptions = useMemo(
    () =>
      subscriptions
        ? [
            ...subscriptions,
            {
              uid: null,
              membershipId: null,
              membershipName: l('no_subscription', {caseAction: 'capitalize'}),
              cancelPolicy: null,
              disabledFromAssessConfig: isNotNilAndNotEmpty(allowedSubscriptionsList) && !anySubscriptions,
              logo: {
                secure_url: null,
              },
            },
          ]
        : [],
    [subscriptions, anySubscriptions],
  )
  const onSubmit = useCallback(() => {
    const valuesToSave = {
      ...values,
      name:
        selectedMemberParticipantIndex === null ? values.name : memberParticipants[selectedMemberParticipantIndex].name,
      avatar: selectedMemberParticipantIndex === null ? values.avatar : null,
      membershipId: selectedSubscriptionIndex ? subscriptionsOptions[selectedSubscriptionIndex]?.uid : null,
      memberParticipantIndex: selectedMemberParticipantIndex,
      subscription:
        selectedSubscriptionIndex !== undefined && selectedSubscriptionIndex !== subscriptionsOptions.length - 1
          ? subscriptionsOptions[selectedSubscriptionIndex]
          : null,
    }
    setValues(valuesToSave as EditParticipantFormType)
    handleSubmit()
  }, [setValues, values, subscriptionsOptions, selectedSubscriptionIndex])
  const onParticipantChange = useCallback(
    (index: number) => {
      setSelectedMemberParticipantIndex(index === memberParticipants.length ? null : index)
      setValues({
        ...values,
        name:
          selectedMemberParticipantIndex === null
            ? values.name
            : memberParticipants[selectedMemberParticipantIndex].name,
        avatar:
          selectedMemberParticipantIndex === null
            ? NO_USER_PHOTO
            : memberParticipants[selectedMemberParticipantIndex].avatar?.secure_url || NO_USER_PHOTO,
      })
    },
    [selectedMemberParticipantIndex, values, setValues],
  )

  return (
    <Box pb={'16px'}>
      {isLoggedIn && (
        <Title
          text={l('selectParticipant')}
          styleType='h3'
          settings={{my: '16px'}}
        />
      )}
      <Flex
        display={isLoggedIn ? 'flex' : 'none'}
        flexDirection='column'
        mb='16px'
      >
        {[...memberParticipants, {name: l('other'), avatar: {secure_url: NO_USER_PHOTO}}].map(
          ({name, avatar}, index) => (
            <Flex
              key={index}
              mt='8px'
              display={omittedMemberParticipants.some((el) => el === index) ? 'none' : 'flex'}
            >
              <RadioButton
                onChange={() => onParticipantChange(index)}
                checked={
                  selectedMemberParticipantIndex === index ||
                  (index === memberParticipants.length && selectedMemberParticipantIndex === null)
                }
                text={
                  <Flex cursor='pointer'>
                    <MemberParticipant
                      name={name}
                      avatar={avatar}
                    />
                  </Flex>
                }
              />
            </Flex>
          ),
        )}
      </Flex>
      <Flex
        display={selectedMemberParticipantIndex === null ? 'flex' : 'none'}
        flexDirection='column'
      >
        <Label>{l('name')}</Label>
        <Input id='name' />
      </Flex>
      {(isNotNilAndNotEmpty(subscriptions) || isNotNilAndNotEmpty(unavailableSubscriptions)) && (
        <Flex
          flexDirection='column'
          mt='16px'
          mb='32px'
          gap='8px'
          maxWidth={['300px', '600px']}
        >
          <Title
            styleType='h3'
            text={l('selectSubscription')}
          />
          {subscriptionsOptions.map((subscription, index) =>
            subscription ? (
              <RadioButton
                onChange={() => setSelectedSubscriptionIndex(index)}
                checked={
                  index === selectedSubscriptionIndex ||
                  (index === subscriptionsOptions.length - 1 && selectedSubscriptionIndex === -1)
                }
                disabled={
                subscription.disabledFromAssessConfig
                  // isNotNilAndNotEmpty(allowedSubscriptionsList) &&
                  // !allowedSubscriptionsList.includes(subscription.membershipId) &&
                  // !anySubscriptions
                }
              >
                <SubscriptionCard
                  name={subscription.membershipName}
                  imageUrl={subscription.logo.secure_url}
                />
              </RadioButton>
            ) : null,
          )}
          {unavailableSubscriptions.map((pass) => {
            const hours = formatOpeningTimes(pass.openingTimes, passHoursOptions)
            return (
              <RadioButton
                key={`unavailable-${pass.membershipBookingId || pass.membershipId}`}
                disabled
                checked={false}
              >
                <Box
                  style={{opacity: 0.6}}
                  data-testid='unavailable-pass'
                >
                  <SubscriptionCard
                    name={pass.name}
                    imageUrl={pass.logo?.secure_url || null}
                    subText={l('pass_unavailable_now')}
                  />
                  {hours && (
                    <P
                      fontSize={'tag'}
                      color={'text-secondary'}
                    >
                      {`${l('pass_usable_on')}: ${hours}`}
                    </P>
                  )}
                </Box>
              </RadioButton>
            )
          })}
        </Flex>
      )}
      <Button onClick={onSubmit}>{l('save')}</Button>
    </Box>
  )
}
